import type { DocumentNode } from "graphql";
import type { TypedDocumentNode } from "@graphql-typed-document-node/core";

// Contentful GraphQL client interface.
interface ContentfulClientOptions {
  preview?: boolean;
  locale?: string;
}

interface QueryOptions extends ContentfulClientOptions {
  [key: string]: unknown;
}

class ContentfulClient {
  private baseUrl = "/api/contentful";

  // Overloaded method signatures for better TypeScript support
  async query<TResult = unknown, TVariables = Record<string, unknown>>(
    query: TypedDocumentNode<TResult, TVariables>,
    variables?: TVariables & ContentfulClientOptions,
  ): Promise<TResult>;
  async query<T = unknown>(query: string, variables?: QueryOptions): Promise<T>;
  async query<TResult = unknown, TVariables = Record<string, unknown>>(
    query: string | TypedDocumentNode<TResult, TVariables>,
    variables?: (TVariables & ContentfulClientOptions) | QueryOptions,
  ): Promise<TResult> {
    // Extract the query string from typed query object or use string directly
    let queryString: string | undefined;
    if (typeof query === "string") {
      queryString = query;
    } else {
      // For GraphQL Code Generator, the query string is in loc.source.body
      if ((query as TypedDocumentNode<TResult, TVariables>).loc?.source?.body) {
        queryString = query.loc!.source.body;
      } else if (
        (query as TypedDocumentNode<TResult, TVariables>).definitions?.[0]?.loc
          ?.source?.body
      ) {
        queryString = query.definitions[0]!.loc!.source.body;
      } else {
        // Fallback: try to use the print function from graphql
        try {
          const { print } = await import("graphql");
          queryString = print(query as DocumentNode);
        } catch (e) {
          console.error("[ContentfulClient] Failed to print query:", e);
          queryString = String(query);
        }
      }
    }

    try {
      const { preview, locale, ...graphqlVariables } = variables || {};

      // Build query parameters for the API endpoint (environment is handled server-side only)
      const params = new URLSearchParams();
      if (preview) params.append("preview", "true");

      const url = `${this.baseUrl}${params.toString() ? `?${params.toString()}` : ""}`;

      // Prepare GraphQL request body
      const requestBody = {
        query: queryString,
        variables: {
          preview: preview || false,
          ...graphqlVariables,
          ...(locale && { locale }),
          // Always include preview parameter to override GraphQL default
        },
      };

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("[ContentfulClient] Error response:", errorText);
        throw new Error(
          `HTTP error! status: ${response.status}, body: ${errorText}`,
        );
      }

      const responseText = await response.text();

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error("[ContentfulClient] Failed to parse JSON:", parseError);
        throw new Error(`Invalid JSON response: ${responseText}`);
      }

      // Handle GraphQL errors with graceful degradation
      if (data.errors) {
        console.warn(
          "[ContentfulClient] GraphQL errors detected:",
          data.errors,
        );

        // Categorize errors to determine if they're fatal
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const fatalErrors = data.errors.filter((error: any) =>
          this.isFatalError(error),
        );
        const nonFatalErrors = data.errors.filter(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (error: any) => !this.isFatalError(error),
        );

        // Log non-fatal errors as warnings
        if (nonFatalErrors.length > 0) {
          console.warn(
            "[ContentfulClient] Non-fatal errors (continuing with partial data):",
            nonFatalErrors.map((e: { message: string }) => e.message),
          );
        }

        // Only throw for fatal errors
        if (fatalErrors.length > 0) {
          console.error(
            "[ContentfulClient] Fatal GraphQL errors:",
            fatalErrors,
          );
          throw new Error(
            `Fatal GraphQL error: ${fatalErrors.map((e: { message: string }) => e.message).join(", ")}`,
          );
        }

        // If we have data despite non-fatal errors, continue with it
        if (data.data) {
          console.info(
            "[ContentfulClient] Returning partial data despite non-fatal errors",
          );
        }
      }

      return data.data || data;
    } catch (error) {
      console.error(
        "[ContentfulClient] Failed to fetch contentful data:",
        error,
      );
      throw error;
    }
  }

  /**
   * Determines if a GraphQL error is fatal (should stop execution)
   * or non-fatal (can continue with partial data)
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private isFatalError(error: any): boolean {
    const message = error.message || "";

    // Non-fatal errors that we can gracefully handle
    const nonFatalPatterns = [
      // Contentful link resolution errors
      /Link from entry .* cannot be resolved/i,
      /Entry .* is not published/i,
      /Asset .* is not published/i,
      /Reference .* could not be resolved/i,
      // Missing optional fields
      /Field .* is not defined/i,
    ];

    // Check if this error matches any non-fatal patterns
    const isNonFatal = nonFatalPatterns.some((pattern) =>
      pattern.test(message),
    );

    if (isNonFatal) {
      return false;
    }

    // Fatal errors that should stop execution
    const fatalPatterns = [
      // Authentication/authorization errors
      /unauthorized/i,
      /forbidden/i,
      /invalid token/i,
      /access denied/i,
      // Query syntax errors
      /syntax error/i,
      /validation error/i,
      /cannot query field/i,
      // Rate limiting
      /rate limit/i,
      /too many requests/i,
    ];

    // Check if this error matches any fatal patterns
    const isFatal = fatalPatterns.some((pattern) => pattern.test(message));

    // Default to fatal for unknown errors to be safe
    return isFatal || !isNonFatal;
  }
}

// Create a singleton instance
export const contentfulClient = new ContentfulClient();
